import { Box, TextField } from "@mui/material";
import DebouncedTextField from "@/components/DebouncedTextField";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import DeleteIcon from "@/components/app/DeleteIcon";
import { DEFAULT_BG_COLOR } from "@/utils/utils";
import {
  FREE_TEXT_LABEL,
  FREE_TEXT_MAX_ROWS,
  FREE_TEXT_PLACEHOLDER,
  useSectionRequirement,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import CodeSearchField from "./CodeSearchField";
import {
  CODE_LIST_MAX_HEIGHT_PX,
  CODE_LIST_ROW_GAP_PX,
  CODE_NOTES_LABEL,
  REQUIRED_MARK,
} from "../initial-f2f-code-constant";
import { splitCode } from "../initial-f2f-utils";
import { CodeBlockLabels, CodeItem } from "../initial-f2f-types";

export interface CodeSelectBlockProps {
  block: CodeBlockLabels;
  options: CodeItem[];
  isLoading: boolean;
  onSearch: (value: string) => void;
  codes: CodeItem[];
  onCodesChange: (codes: CodeItem[]) => void;
  freeText: string;
  onFreeTextChange: (value: string) => void;
  isMultiple?: boolean;
}

const CodeSelectBlock = ({
  block,
  options,
  isLoading,
  onSearch,
  codes,
  onCodesChange,
  freeText,
  onFreeTextChange,
  isMultiple,
}: CodeSelectBlockProps) => {
  const { isRequired, missingError } = useSectionRequirement();
  const requiredError = missingError(codes);
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {block.heading && (
        <Text fontWeight="bold" c="text.secondary" size={12}>
          {block.heading}
          {isRequired && (
            <Text component="span" c="error.main" ml={0.5}>
              {REQUIRED_MARK}
            </Text>
          )}
        </Text>
      )}
      <CodeSearchField
        options={options}
        isLoading={isLoading}
        placeholder={block.placeholder}
        buttonLabel={block.buttonLabel}
        onSearch={onSearch}
        addedCodes={codes}
        isMultiple={isMultiple}
        isSearchOnly={block.isSearchOnly}
        duplicateMessage={block.duplicateMessage}
        onAdd={(added) => onCodesChange([...codes, ...added])}
      />
      {requiredError && (
        <Text size={12} c="error.main">
          {requiredError}
        </Text>
      )}
      {block.hint && (
        <Text size={12} c="text.secondary">
          {block.hint}
        </Text>
      )}
      {codes.length > 0 && (
        <Box
          display="flex"
          flexDirection="column"
          gap={`${CODE_LIST_ROW_GAP_PX}px`}
          maxHeight={CODE_LIST_MAX_HEIGHT_PX}
          overflow="auto"
        >
          {codes.map((code, index) => {
            const parts = splitCode(code.code);
            return (
              <Flex
                key={code.value}
                alignItems="center"
                gap={1.5}
                flexShrink={0}
                sx={{ bgcolor: DEFAULT_BG_COLOR, borderRadius: 1, p: 1 }}
              >
                {block.isNumbered && (
                  <Text fontWeight={600} sx={{ flexShrink: 0 }}>
                    {index + 1}.
                  </Text>
                )}
                <TextField
                  size="small"
                  fullWidth
                  label={block.nameLabel}
                  InputLabelProps={{ shrink: true }}
                  value={parts.name}
                  inputProps={{ readOnly: true }}
                />
                {block.codeLabel && (
                  <TextField
                    size="small"
                    label={block.codeLabel}
                    InputLabelProps={{ shrink: true }}
                    value={parts.code}
                    inputProps={{ readOnly: true }}
                    sx={{ width: 140, flexShrink: 0 }}
                  />
                )}
                <DebouncedTextField
                  size="small"
                  fullWidth
                  label={CODE_NOTES_LABEL}
                  placeholder={CODE_NOTES_LABEL}
                  InputLabelProps={{ shrink: true }}
                  value={code.notes ?? ""}
                  onChange={(notes) =>
                    onCodesChange(
                      codes.map((it) =>
                        it.value === code.value ? { ...it, notes } : it
                      )
                    )
                  }
                />
                <DeleteIcon
                  aria-label={`Remove ${code.code}`}
                  onClick={() =>
                    onCodesChange(codes.filter((it) => it.value !== code.value))
                  }
                />
              </Flex>
            );
          })}
        </Box>
      )}
      <Text fontWeight="bold">{FREE_TEXT_LABEL}</Text>
      <DebouncedTextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        maxRows={FREE_TEXT_MAX_ROWS}
        placeholder={FREE_TEXT_PLACEHOLDER}
        value={freeText}
        onChange={onFreeTextChange}
      />
    </Box>
  );
};

export default CodeSelectBlock;
